import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';

export function PricingSection() {
	const plans = [
		{
			name: 'Free',
			price: '$0',
			period: '/month',
			description: 'Perfect for getting started',
			features: [
				'Up to 100 verified emails',
				'Email validation'
			],
			cta: 'Get Started Free',
			popular: false
		},
		{
			name: 'Starter',
			price: '$100',
			period: '/month',
			description: 'For growing businesses',
			features: [
				'10,000 verified emails/month',
				'Email validation & verification',
				'Priority support'
			],
			cta: 'Get Started',
			popular: true
		},
		{
			name: 'Professional',
			price: '$200',
			period: '/month',
			description: 'For scaling sales teams',
			features: [
				'20,000 verified emails/month',
				'Email validation & verification',
				'Priority support',
				'Analytics dashboard'
			],
			cta: 'Get Started',
			popular: false
		},
		{
			name: 'Enterprise',
			price: '$500',
			period: '/month',
			description: 'For large organizations',
			features: [
				'50,000 verified emails/month',
				'Email validation & verification',
				'Dedicated support',
				'API access'
			],
			cta: 'Contact Sales',
			popular: false
		}
	];

	return (
		<section className="py-20 px-6 lg:px-8 bg-gray-50">
			<div className="mx-auto max-w-6xl">
				<div className="text-center mb-16">
					<h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
						Simple, Transparent Pricing
					</h2>
					<p className="mt-4 text-lg text-gray-600">
						Pay only for verified emails. No setup fees, no hidden costs.
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
					{plans.map((plan, index) => (
						<div
							key={index}
							className={`bg-white rounded-3xl p-8 shadow-sm border ${plan.popular
								? 'border-blue-500 shadow-lg ring-2 ring-blue-500 ring-opacity-20'
								: 'border-gray-200'
								}`}
						>
							{plan.popular && (
								<div className="text-center mb-4">
									<span className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
										Most Popular
									</span>
								</div>
							)}

							<div className="text-center mb-8">
								<h3 className="text-2xl font-bold text-gray-900 mb-2">
									{plan.name}
								</h3>
								<div className="flex items-end justify-center mb-4">
									<span className="text-4xl font-bold text-gray-900">
										{plan.price}
									</span>
									<span className="text-gray-600 ml-1">{plan.period}</span>
								</div>
								<p className="text-gray-600">{plan.description}</p>
							</div>

							<ul className="space-y-4 mb-8">
								{plan.features.map((feature, featureIndex) => (
									<li key={featureIndex} className="flex items-start space-x-3">
										<Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
										<span className="text-gray-700">{feature}</span>
									</li>
								))}
							</ul>

							<Button
								className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 ${plan.popular
									? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600'
									: 'bg-gray-900 text-white hover:bg-gray-800'
									}`}
								asChild
							>
								<Link href="/signup">{plan.cta}</Link>
							</Button>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
